import { useEffect, useState } from 'react'
import { isEqual } from 'lodash-es'
import {
  AgxColumn,
  SellersDisclosureUploadModel,
  AgxControlledCheckbox,
  DocumentTypes,
  EditablePdfButtonGroup,
  CustomPdfScript,
} from '@urbanx/agx-ui-components'
import EditablePdfUpload from 'pdf/EditablePdfUpload/EditablePdfUpload'

interface Props {
  id: string
  label?: string
  multiple?: boolean
  extensions?: string[]
  documentType?: DocumentTypes
  isSortable?: boolean
  campaignId: string
  buttonGroups: EditablePdfButtonGroup[]
  customScript?: CustomPdfScript
  onChange: (change: {
    id: string
    value: SellersDisclosureUploadModel
  }) => void
  defaultValue?: SellersDisclosureUploadModel
  getAuthToken: () => Promise<string | void>
  uploadFile?: (...args: any) => Promise<any>
  editFile?: (...args: any) => Promise<any>
  deleteFile?: (
    authToken: string,
    filePath: string
  ) => Promise<number | undefined>
  getFileLink?: (authToken: string, filePath: string) => Promise<any>
  getLicenseKey?: (authToken: string) => Promise<string | undefined>
  required: boolean
  validate: boolean
  requiredPdfFields?: string[]
}

const SellersDisclosureUpload = ({
  id,
  defaultValue,
  getAuthToken,
  campaignId,
  buttonGroups,
  customScript,
  onChange,
  uploadFile,
  editFile,
  deleteFile,
  getFileLink,
  getLicenseKey,
  required,
  validate,
  multiple = false,
  documentType = DocumentTypes.SellerDisclosure,
  requiredPdfFields,
}: Props) => {
  const [markAsExempt, setMarkAsExempt] = useState(
    defaultValue?.markAsExempt ?? false
  )
  const [sellersDisclosureDocuments, setSellersDisclosureDocuments] = useState(
    defaultValue?.sellersDisclosureDocuments
  )

  useEffect(() => {
    const nextMarkAsExempt = defaultValue?.markAsExempt ?? false
    const nextDocuments = defaultValue?.sellersDisclosureDocuments

    setMarkAsExempt((prev) =>
      prev === nextMarkAsExempt ? prev : nextMarkAsExempt
    )
    setSellersDisclosureDocuments((prev) =>
      isEqual(prev, nextDocuments) ? prev : nextDocuments
    )
  }, [defaultValue])

  useEffect(() => {
    onChange({
      id,
      value: {
        markAsExempt: markAsExempt,
        sellersDisclosureDocuments: sellersDisclosureDocuments,
      },
    })
  }, [id, markAsExempt, onChange, sellersDisclosureDocuments])

  return (
    <AgxColumn extraLargeGap>
      <EditablePdfUpload
        id={`sellersDisclosureUploadDocument-${id}`}
        key={`sellersDisclosureUploadDocument-${id}-${markAsExempt}`}
        defaultValue={sellersDisclosureDocuments}
        campaignId={campaignId}
        buttonGroups={buttonGroups}
        customScript={customScript}
        documentType={documentType}
        getAuthToken={getAuthToken}
        onValueChanged={({ value: uploadedFiles }) =>
          setSellersDisclosureDocuments((prev) =>
            isEqual(prev, uploadedFiles) ? prev : uploadedFiles
          )
        }
        uploadFile={uploadFile}
        deleteFile={deleteFile}
        editFile={editFile}
        getFileLink={getFileLink}
        getLicenseKey={getLicenseKey}
        required={!markAsExempt && required}
        requiredPdfFields={requiredPdfFields}
        validate={validate}
        multiple={multiple}
      />

      <AgxControlledCheckbox
        key={`markAsExempt-${id}`}
        checked={markAsExempt}
        onValueChanged={(value) => setMarkAsExempt(value)}
        label="Mark as Exempt"
        naked
        id={id}
      />
    </AgxColumn>
  )
}

export default SellersDisclosureUpload
